import { useEffect, useRef } from 'react'
import { useWidgetStore } from './WidgetContext'
import { useUtilsStore } from './UtilsContext'
import { useLocaleStore } from './LocaleContext'
import { useAdaptiveCardStore } from './AdaptiveCardContext'
import { useViewStore } from './ViewContext'
import { useSpeechStore } from './SpeechContext'

import type { Choice } from './AdaptiveCardContext'
import { BaseAgentData, CustomMenu } from '../../models/types'
import { DetailedAttachmentAction, RetorikActivity } from '../../models/activityTypes'
import { Mode, RecognitionState } from '../../models/enums'
import { useRetorikStore } from './RetorikContext'
import { useActivityStore } from './ActivityContext'
import { useDirectlineStore } from './DirectlineContext'

interface InputOutputHandlerProps {
  instanceId?: string
}

const InputOutputHandler = ({ instanceId }: InputOutputHandlerProps): null => {
  const { closeWidget, openWidget, toggleDisplay, toggleWidget } = useWidgetStore((state) => state.actions)
  const {
    addDetailedAttachmentTrigger,
    hideAgent,
    lockClickedButton,
    resetUtilsStore,
    sendActivity,
    setCurrentCustomView,
    setCurrentSubView,
    setDashboardOpened,
    setModalFullscreenContent,
    setPrintingCallback,
    showAgent
  } = useUtilsStore((state) => state.actions)
  const { setLocale, resetLocaleStore, addDumpedLanguageFallback, addReplacementLocale } = useLocaleStore((state) => state.actions)
  const { setValue, setCurrentId, setToggleValue, addCurrentToggleId, resetAdaptiveCardStore } = useAdaptiveCardStore((state) => state.actions)
  const { setVisibility, toggleVisibility } = useViewStore((state) => state.actions)
  const { setActiveRecognitionState, cancelCurrentUtterance, setMuted, setMode, playExternalSynthesis, resetSpeechStore, toggleMicrophone, askPermission } = useSpeechStore((state) => state.actions)
  const isMicrophoneAllowed = useSpeechStore((state) => state.isMicrophoneAllowed)
  const isMicrophoneAllowedRef = useRef<boolean>(isMicrophoneAllowed)
  const { setDisplaySubtitles, toggleSubtitles, setAgentData, setLoaderClosed, resetRetorikStore } = useRetorikStore((state) => state.actions)
  const resetActivityStore = useActivityStore((state) => state.actions.resetActivityStore)
  const { sendMessage, sendEvent, pauseConversation, resumeConversation, sendMessageToChatbot, addExternalActivityHandler, setExternalActivityHandlers, resetDirectlineStore } = useDirectlineStore(
    (state) => state.actions
  )

  const handleCustomEvent = (event: CustomEvent, method: any): void => {
    // Check if an instance id is given to use this method in a single instance of Retorik Framework when there are several
    const checkInstanceId = event.detail?.instanceId ? (instanceId ? event.detail.instanceId === instanceId : false) : true

    if (checkInstanceId) {
      // Add the parameters to the function depending on their number (up to 3)
      try {
        switch (event.detail?.values?.length) {
          case 0:
          case undefined:
            method()
            break
          case 1:
            method(event.detail.values[0])
            break
          case 2:
            method(event.detail.values[0], event.detail.values[1])
            break
          case 3:
            method(event.detail.values[0], event.detail.values[1], event.detail.values[2])
            break
        }
      } catch (e) {
        console.error(`Retorik Framework > failed to use ${method}`)
      }
    }
  }

  useEffect(() => {
    isMicrophoneAllowedRef.current = isMicrophoneAllowed
  }, [isMicrophoneAllowed])

  // Use refs to get updated states in native javascript events
  const handleToggleMicrophone = () => {
    isMicrophoneAllowedRef.current ? toggleMicrophone() : askPermission()
  }

  useEffect(() => {
    // Widget store events
    document.addEventListener('retorik_openWidget', (e: CustomEvent) => handleCustomEvent(e, openWidget))
    document.addEventListener('retorik_closeWidget', (e: CustomEvent) => handleCustomEvent(e, closeWidget))
    document.addEventListener('retorik_toggleWidget', (e: CustomEvent) => handleCustomEvent(e, toggleWidget))
    document.addEventListener('retorik_toggleDisplay', (e: CustomEvent) => handleCustomEvent(e, toggleDisplay))

    // Utils store events
    document.addEventListener('retorik_showAgent', (e: CustomEvent) => handleCustomEvent(e, showAgent))
    document.addEventListener('retorik_hideAgent', (e: CustomEvent) => handleCustomEvent(e, hideAgent))
    document.addEventListener('retorik_resetUtilsStore', (e: CustomEvent) => handleCustomEvent(e, resetUtilsStore))
    document.addEventListener('retorik_lockClickedButton', (e: CustomEvent) => handleCustomEvent(e, lockClickedButton))
    document.addEventListener('retorik_setCurrentCustomView', (e: CustomEvent) => handleCustomEvent(e, setCurrentCustomView))
    document.addEventListener('retorik_setCurrentSubView', (e: CustomEvent) => handleCustomEvent(e, setCurrentSubView))
    document.addEventListener('retorik_sendActivity', (e: CustomEvent) => handleCustomEvent(e, sendActivity))
    document.addEventListener('retorik_setModalFullscreenContent', (e: CustomEvent) => handleCustomEvent(e, setModalFullscreenContent))
    document.addEventListener('retorik_addDetailedAttachmentTrigger', (e: CustomEvent) => handleCustomEvent(e, addDetailedAttachmentTrigger))
    document.addEventListener('retorik_setPrintingCallback', (e: CustomEvent) => handleCustomEvent(e, setPrintingCallback))
    document.addEventListener('retorik_setDashboardOpened', (e: CustomEvent) => handleCustomEvent(e, setDashboardOpened))

    // Locale store events
    document.addEventListener('retorik_setLocale', (e: CustomEvent) => handleCustomEvent(e, setLocale))
    document.addEventListener('retorik_resetLocaleStore', (e: CustomEvent) => handleCustomEvent(e, resetLocaleStore))
    document.addEventListener('retorik_addDumpedLanguageFallback', (e: CustomEvent) => handleCustomEvent(e, addDumpedLanguageFallback))
    document.addEventListener('retorik_addReplacementLocale', (e: CustomEvent) => handleCustomEvent(e, addReplacementLocale))

    // Adaptive cards store events
    document.addEventListener('retorik_setValue', (e: CustomEvent) => handleCustomEvent(e, setValue))
    document.addEventListener('retorik_setCurrentId', (e: CustomEvent) => handleCustomEvent(e, setCurrentId))
    document.addEventListener('retorik_setToggleValue', (e: CustomEvent) => handleCustomEvent(e, setToggleValue))
    document.addEventListener('retorik_addCurrentToggleId', (e: CustomEvent) => handleCustomEvent(e, addCurrentToggleId))
    document.addEventListener('retorik_resetAdaptiveCardStore', (e: CustomEvent) => handleCustomEvent(e, resetAdaptiveCardStore))

    // View store utils
    document.addEventListener('retorik_setVisibility', (e: CustomEvent) => handleCustomEvent(e, setVisibility))
    document.addEventListener('retorik_toggleVisibility', (e: CustomEvent) => handleCustomEvent(e, toggleVisibility))

    // Speech store utils
    document.addEventListener('retorik_setActiveRecognitionState', (e: CustomEvent) => handleCustomEvent(e, setActiveRecognitionState))
    document.addEventListener('retorik_cancelCurrentUtterance', (e: CustomEvent) => handleCustomEvent(e, cancelCurrentUtterance))
    document.addEventListener('retorik_toggleMicrophone', handleToggleMicrophone)
    document.addEventListener('retorik_setMuted', (e: CustomEvent) => handleCustomEvent(e, setMuted))
    document.addEventListener('retorik_playExternalSynthesis', (e: CustomEvent) => handleCustomEvent(e, playExternalSynthesis))
    document.addEventListener('retorik_resetSpeechStore', (e: CustomEvent) => handleCustomEvent(e, resetSpeechStore))

    // Retorik store utils
    document.addEventListener('retorik_setDisplaySubtitles', (e: CustomEvent) => handleCustomEvent(e, setDisplaySubtitles))
    document.addEventListener('retorik_toggleSubtitles', (e: CustomEvent) => handleCustomEvent(e, toggleSubtitles))
    document.addEventListener('retorik_setAgentData', (e: CustomEvent) => handleCustomEvent(e, setAgentData))
    document.addEventListener('retorik_setMode', (e: CustomEvent) => handleCustomEvent(e, setMode))
    document.addEventListener('retorik_setLoaderClosed', (e: CustomEvent) => handleCustomEvent(e, setLoaderClosed))
    document.addEventListener('retorik_resetRetorikStore', (e: CustomEvent) => handleCustomEvent(e, resetRetorikStore))

    // Activity store utils
    document.addEventListener('retorik_resetActivityStore', (e: CustomEvent) => handleCustomEvent(e, resetActivityStore))

    // Directline store utils
    document.addEventListener('retorik_sendMessage', (e: CustomEvent) => handleCustomEvent(e, sendMessage))
    document.addEventListener('retorik_sendEvent', (e: CustomEvent) => handleCustomEvent(e, sendEvent))
    document.addEventListener('retorik_pauseConversation', (e: CustomEvent) => handleCustomEvent(e, pauseConversation))
    document.addEventListener('retorik_resumeConversation', (e: CustomEvent) => handleCustomEvent(e, resumeConversation))
    document.addEventListener('retorik_sendMessageToChatbot', (e: CustomEvent) => handleCustomEvent(e, sendMessageToChatbot))
    document.addEventListener('retorik_addExternalActivityHandler', (e: CustomEvent) => handleCustomEvent(e, addExternalActivityHandler))
    document.addEventListener('retorik_setExternalActivityHandlers', (e: CustomEvent) => handleCustomEvent(e, setExternalActivityHandlers))
    document.addEventListener('retorik_resetDirectlineStore', (e: CustomEvent) => handleCustomEvent(e, resetDirectlineStore))

    return () => {
      document.removeEventListener('retorik_openWidget', (e: CustomEvent) => handleCustomEvent(e, openWidget))
      document.removeEventListener('retorik_closeWidget', (e: CustomEvent) => handleCustomEvent(e, closeWidget))
      document.removeEventListener('retorik_toggleWidget', (e: CustomEvent) => handleCustomEvent(e, toggleWidget))
      document.removeEventListener('retorik_toggleDisplay', (e: CustomEvent) => handleCustomEvent(e, toggleDisplay))

      document.removeEventListener('retorik_showAgent', (e: CustomEvent) => handleCustomEvent(e, showAgent))
      document.removeEventListener('retorik_hideAgent', (e: CustomEvent) => handleCustomEvent(e, hideAgent))
      document.removeEventListener('retorik_resetUtilsStore', (e: CustomEvent) => handleCustomEvent(e, resetUtilsStore))
      document.removeEventListener('retorik_lockClickedButton', (e: CustomEvent) => handleCustomEvent(e, lockClickedButton))
      document.removeEventListener('retorik_setCurrentCustomView', (e: CustomEvent) => handleCustomEvent(e, setCurrentCustomView))
      document.removeEventListener('retorik_setCurrentSubView', (e: CustomEvent) => handleCustomEvent(e, setCurrentSubView))
      document.removeEventListener('retorik_sendActivity', (e: CustomEvent) => handleCustomEvent(e, sendActivity))
      document.removeEventListener('retorik_setModalFullscreenContent', (e: CustomEvent) => handleCustomEvent(e, setModalFullscreenContent))
      document.removeEventListener('retorik_addDetailedAttachmentTrigger', (e: CustomEvent) => handleCustomEvent(e, addDetailedAttachmentTrigger))
      document.removeEventListener('retorik_setPrintingCallback', (e: CustomEvent) => handleCustomEvent(e, setPrintingCallback))
      document.removeEventListener('retorik_setDashboardOpened', (e: CustomEvent) => handleCustomEvent(e, setDashboardOpened))

      document.removeEventListener('retorik_setLocale', (e: CustomEvent) => handleCustomEvent(e, setLocale))
      document.removeEventListener('retorik_resetLocaleStore', (e: CustomEvent) => handleCustomEvent(e, resetLocaleStore))
      document.removeEventListener('retorik_addDumpedLanguageFallback', (e: CustomEvent) => handleCustomEvent(e, addDumpedLanguageFallback))
      document.removeEventListener('retorik_addReplacementLocale', (e: CustomEvent) => handleCustomEvent(e, addReplacementLocale))

      document.removeEventListener('retorik_setValue', (e: CustomEvent) => handleCustomEvent(e, setValue))
      document.removeEventListener('retorik_setCurrentId', (e: CustomEvent) => handleCustomEvent(e, setCurrentId))
      document.removeEventListener('retorik_setToggleValue', (e: CustomEvent) => handleCustomEvent(e, setToggleValue))
      document.removeEventListener('retorik_addCurrentToggleId', (e: CustomEvent) => handleCustomEvent(e, addCurrentToggleId))
      document.removeEventListener('retorik_resetAdaptiveCardStore', (e: CustomEvent) => handleCustomEvent(e, resetAdaptiveCardStore))

      document.removeEventListener('retorik_setVisibility', (e: CustomEvent) => handleCustomEvent(e, setVisibility))
      document.removeEventListener('retorik_toggleVisibility', (e: CustomEvent) => handleCustomEvent(e, toggleVisibility))

      document.removeEventListener('retorik_setActiveRecognitionState', (e: CustomEvent) => handleCustomEvent(e, setActiveRecognitionState))
      document.removeEventListener('retorik_cancelCurrentUtterance', (e: CustomEvent) => handleCustomEvent(e, cancelCurrentUtterance))
      document.removeEventListener('retorik_toggleMicrophone', handleToggleMicrophone)
      document.removeEventListener('retorik_setMuted', (e: CustomEvent) => handleCustomEvent(e, setMuted))
      document.removeEventListener('retorik_playExternalSynthesis', (e: CustomEvent) => handleCustomEvent(e, playExternalSynthesis))
      document.removeEventListener('retorik_resetSpeechStore', (e: CustomEvent) => handleCustomEvent(e, resetSpeechStore))

      document.removeEventListener('retorik_setDisplaySubtitles', (e: CustomEvent) => handleCustomEvent(e, setDisplaySubtitles))
      document.removeEventListener('retorik_toggleSubtitles', (e: CustomEvent) => handleCustomEvent(e, toggleSubtitles))
      document.removeEventListener('retorik_setAgentData', (e: CustomEvent) => handleCustomEvent(e, setAgentData))
      document.removeEventListener('retorik_setMode', (e: CustomEvent) => handleCustomEvent(e, setMode))
      document.removeEventListener('retorik_setLoaderClosed', (e: CustomEvent) => handleCustomEvent(e, setLoaderClosed))
      document.removeEventListener('retorik_resetRetorikStore', (e: CustomEvent) => handleCustomEvent(e, resetRetorikStore))

      document.removeEventListener('retorik_resetActivityStore', (e: CustomEvent) => handleCustomEvent(e, resetActivityStore))

      document.removeEventListener('retorik_sendMessage', (e: CustomEvent) => handleCustomEvent(e, sendMessage))
      document.removeEventListener('retorik_sendEvent', (e: CustomEvent) => handleCustomEvent(e, sendEvent))
      document.removeEventListener('retorik_pauseConversation', (e: CustomEvent) => handleCustomEvent(e, pauseConversation))
      document.removeEventListener('retorik_resumeConversation', (e: CustomEvent) => handleCustomEvent(e, resumeConversation))
      document.removeEventListener('retorik_sendMessageToChatbot', (e: CustomEvent) => handleCustomEvent(e, sendMessageToChatbot))
      document.removeEventListener('retorik_addExternalActivityHandler', (e: CustomEvent) => handleCustomEvent(e, addExternalActivityHandler))
      document.removeEventListener('retorik_setExternalActivityHandlers', (e: CustomEvent) => handleCustomEvent(e, setExternalActivityHandlers))
      document.removeEventListener('retorik_resetDirectlineStore', (e: CustomEvent) => handleCustomEvent(e, resetDirectlineStore))
    }
  }, [])

  return null
}

export default InputOutputHandler

const eventDispatch = (name: string, values: Array<any>, instanceId?: string): void => {
  document.dispatchEvent(
    new CustomEvent(name, {
      detail: {
        instanceId: instanceId,
        values: values
      }
    })
  )
}

// Widget utils
const openWidget = (instanceId?: string) => eventDispatch('retorik_openWidget', [], instanceId)
const closeWidget = (instanceId?: string) => eventDispatch('retorik_closeWidget', [], instanceId)
const toggleWidget = (instanceId?: string) => eventDispatch('retorik_toggleWidget', [], instanceId)
const toggleDisplay = (instanceId?: string) => eventDispatch('retorik_toggleDisplay', [], instanceId)

// Utils utils
const showAgent = (instanceId?: string) => eventDispatch('retorik_showAgent', [], instanceId)
const hideAgent = (instanceId?: string) => eventDispatch('retorik_hideAgent', [], instanceId)
const resetUtilsStore = (instanceId?: string) => eventDispatch('retorik_resetUtilsStore', [], instanceId)
const lockClickedButton = (instanceId?: string) => eventDispatch('retorik_lockClickedButton', [], instanceId)
const setCurrentCustomView = (value: CustomMenu, instanceId?: string) => eventDispatch('retorik_setCurrentCustomView', [value], instanceId)
const setCurrentSubView = (value: number, instanceId?: string) => eventDispatch('retorik_setCurrentSubView', [value], instanceId)
/**
 * deprecated since v3.2.6
 */
const sendActivity = (value: RetorikActivity | undefined, instanceId?: string) => eventDispatch('retorik_sendActivity', [value], instanceId)
const addDetailedAttachmentTrigger = (value: DetailedAttachmentAction, instanceId?: string) => eventDispatch('retorik_addDetailedAttachmentTrigger', [value], instanceId)
const setModalFullscreenContent = (value: JSX.Element | null, instanceId?: string) => eventDispatch('retorik_setModalFullscreenContent', [value], instanceId)
const setPrintingCallback = (value: ((url: string) => void) | null, instanceId?: string) => eventDispatch('retorik_setPrintingCallback', [value], instanceId)
const setDashboardOpened = (value: boolean, instanceId?: string) => eventDispatch('retorik_setDashboardOpened', [value], instanceId)

// Locale utils
const setLocale = (value: string, instanceId?: string) => eventDispatch('retorik_setLocale', [value], instanceId)
const resetLocaleStore = (instanceId?: string) => eventDispatch('retorik_resetLocaleStore', [], instanceId)
const addDumpedLanguageFallback = (key: string, value: string, instanceId?: string) => eventDispatch('retorik_addDumpedLanguageFallback', [key, value], instanceId)
const addReplacementLocale = (key: string, value: string, instanceId?: string) => eventDispatch('retorik_addReplacementLocale', [key, value], instanceId)

// Adaptive cards utils
const setValue = (id: string, value: string | number | Array<string> | Array<number> | Choice | boolean) => eventDispatch('retorik_setValue', [id, value], undefined)
const setCurrentId = (value: string | undefined) => eventDispatch('retorik_setCurrentId', [value], undefined)
const setToggleValue = (id: string, value: boolean) => eventDispatch('retorik_setToggleValue', [id, value], undefined)
const addCurrentToggleId = (id: string, defaultValue: boolean) => eventDispatch('retorik_addCurrentToggleId', [id, defaultValue], undefined)
const resetAdaptiveCardStore = (instanceId?: string) => eventDispatch('retorik_resetAdaptiveCardStore', [], instanceId)

// View utils
const setVisibility = (value: string, instanceId?: string) => eventDispatch('retorik_setVisibility', [value], instanceId)
const toggleVisibility = (instanceId?: string) => eventDispatch('retorik_toggleVisibility', [], instanceId)

// Speech utils
const setActiveRecognitionState = (value: RecognitionState, instanceId?: string) => eventDispatch('retorik_setActiveRecognitionState', [value], instanceId)
const cancelCurrentUtterance = (instanceId?: string) => eventDispatch('retorik_cancelCurrentUtterance', [], instanceId)
const toggleMicrophone = (instanceId?: string) => eventDispatch('retorik_toggleMicrophone', [], instanceId)
const setMuted = (value: boolean, instanceId?: string) => eventDispatch('retorik_setMuted', [value], instanceId)
const playExternalSynthesis = (value: string, instanceId?: string) => eventDispatch('retorik_playExternalSynthesis', [value], instanceId)
const resetSpeechStore = (instanceId?: string) => eventDispatch('retorik_resetSpeechStore', [], instanceId)

// Retorik utils
const setDisplaySubtitles = (value: boolean, instanceId?: string) => eventDispatch('retorik_setDisplaySubtitles', [value], instanceId)
const toggleSubtitles = (instanceId?: string) => eventDispatch('retorik_toggleSubtitles', [], instanceId)
const setAgentData = (value: BaseAgentData, instanceId?: string) => eventDispatch('retorik_setAgentData', [value], instanceId)
const setMode = (value: Mode, instanceId?: string) => eventDispatch('retorik_setMode', [value], instanceId)
const setLoaderClosed = (value: boolean, instanceId?: string) => eventDispatch('retorik_setLoaderClosed', [value], instanceId)
const resetRetorikStore = (instanceId?: string) => eventDispatch('retorik_resetRetorikStore', [], instanceId)

// Activity utils
const resetActivityStore = (instanceId?: string) => eventDispatch('retorik_resetActivityStore', [], instanceId)

// Directline utils
const sendMessage = (text?: string, value?: Record<string, any>, instanceId?: string) => eventDispatch('retorik_sendMessage', [text, value], instanceId)
const sendEvent = (name: string, value?: Record<string, any> | null, instanceId?: string) => eventDispatch('retorik_sendEvent', [name, value], instanceId)
const pauseConversation = (instanceId?: string) => eventDispatch('retorik_pauseConversation', [], instanceId)
const resumeConversation = (instanceId?: string) => eventDispatch('retorik_resumeConversation', [], instanceId)
const sendMessageToChatbot = (value: string, instanceId?: string) => eventDispatch('retorik_sendMessageToChatbot', [value], instanceId)
const addExternalActivityHandler = (value: (action: any) => boolean, instanceId?: string) => eventDispatch('retorik_addExternalActivityHandler', [value], instanceId)
const setExternalActivityHandlers = (value: Array<(action: any) => boolean>, instanceId?: string) => eventDispatch('retorik_setExternalActivityHandlers', [value], instanceId)
const resetDirectlineStore = (instanceId?: string) => eventDispatch('retorik_resetDirectlineStore', [], instanceId)

export {
  openWidget,
  closeWidget,
  toggleWidget,
  toggleDisplay,
  showAgent,
  hideAgent,
  resetUtilsStore,
  lockClickedButton,
  setCurrentCustomView,
  setCurrentSubView,
  sendActivity,
  addDetailedAttachmentTrigger,
  setModalFullscreenContent,
  setPrintingCallback,
  setDashboardOpened,
  setLocale,
  resetLocaleStore,
  addDumpedLanguageFallback,
  addReplacementLocale,
  setValue,
  setCurrentId,
  setToggleValue,
  addCurrentToggleId,
  resetAdaptiveCardStore,
  setVisibility,
  toggleVisibility,
  setActiveRecognitionState,
  cancelCurrentUtterance,
  toggleMicrophone,
  setMuted,
  playExternalSynthesis,
  resetSpeechStore,
  setDisplaySubtitles,
  toggleSubtitles,
  setAgentData,
  setMode,
  setLoaderClosed,
  resetRetorikStore,
  resetActivityStore,
  sendMessage,
  sendEvent,
  pauseConversation,
  resumeConversation,
  sendMessageToChatbot,
  addExternalActivityHandler,
  setExternalActivityHandlers,
  resetDirectlineStore
}
